import React, { useState, useEffect, useRef } from 'react';
import { useWhatsApp } from '../hooks/useWhatsApp';
import { useMessages } from '../hooks/useMessages';
import { useToast } from '../context/ToastContext';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Modal } from '../components/common/Modal';
import { WhatsAppAPI } from '../lib/whatsapp/api';

export const WhatsApp: React.FC = () => {
  const { numbers, config, loading, createNumber, deleteNumber, saveConfig, validatePhoneNumber } = useWhatsApp();
  const { messages, updateMessage, fetchMessages } = useMessages();
  const { showToast } = useToast();
  const numberFormRef = useRef<HTMLFormElement>(null);
  const [isNumberModalOpen, setIsNumberModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    status: 'active' as 'active' | 'inactive',
  });
  const [configData, setConfigData] = useState({
    apiKey: '',
    phoneNumberId: '',
  });

  useEffect(() => {
    if (config) {
      setConfigData({
        apiKey: config.api_token || '',
        phoneNumberId: config.phone_number_id || '',
      });
    }
  }, [config]);

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      await saveConfig({
        api_token: configData.apiKey,
        phone_number_id: configData.phoneNumberId,
      });
      showToast('Configuração salva com sucesso!', 'success');
    } catch (error: any) {
      showToast(error.message || 'Erro ao salvar configuração', 'error');
    }
  };

  const handleCreateNumber = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = validatePhoneNumber(formData.phone);
    if (!validation.valid) {
      showToast(validation.message || 'Número inválido', 'error');
      return;
    }

    try {
      await createNumber({
        ...formData,
        phone: validation.formatted || formData.phone,
      });
      showToast('Número vinculado com sucesso!', 'success');
      setIsNumberModalOpen(false);
      setFormData({ name: '', phone: '', status: 'active' });
    } catch (error: any) {
      showToast(error.message || 'Erro ao vincular número', 'error');
    }
  };

  const handleDeleteNumber = async (id: string) => {
    if (confirm('Tem certeza que deseja desvincular este número?')) {
      try {
        await deleteNumber(id);
        showToast('Número desvinculado com sucesso!', 'success');
      } catch (error: any) {
        showToast(error.message || 'Erro ao desvincular número', 'error');
      }
    }
  };

  const processQueue = async () => {
    if (!config?.api_token || !config?.phone_number_id) {
      showToast('Configure a API Kapso primeiro', 'error');
      return;
    }

    const api = new WhatsAppAPI({
      apiKey: config.api_token,
      phoneNumberId: config.phone_number_id,
    });

    const pendingMessages = messages.filter(m => m.status === 'pending' || m.status === 'failed');
    
    if (pendingMessages.length === 0) {
      showToast('Nenhuma mensagem pendente', 'warning');
      return;
    }

    for (const message of pendingMessages) {
      try {
        await updateMessage(message.id, { status: 'sending' });
        
        await api.sendMessage(message.recipient_phone, message.content);
        
        await updateMessage(message.id, { 
          status: 'sent',
          sent_at: new Date().toISOString(),
        });
        
        showToast(`Mensagem enviada para ${message.recipient_name}`, 'success');
      } catch (error: any) {
        const attempts = (message.attempts || 0) + 1;
        const maxAttempts = 3;

        if (attempts >= maxAttempts) {
          await updateMessage(message.id, { 
            status: 'failed',
            error: error.message,
            attempts,
          });
          showToast(`Falha ao enviar mensagem: ${error.message}`, 'error');
        } else {
          await updateMessage(message.id, { 
            status: 'pending',
            error: error.message,
            attempts,
          });
          showToast(`Tentativa ${attempts} falhou. Nova tentativa será feita.`, 'warning');
        }
      }
    }

    await fetchMessages();
  };

  if (loading) {
    return <div className="text-center py-8">Carregando configuração...</div>;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-medium">Integração WhatsApp</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="font-medium mb-4">Configuração API</h3>
          <form onSubmit={handleSaveConfig}>
            <Input
              label="Chave da API Kapso"
              type="password"
              value={configData.apiKey}
              onChange={(e) => setConfigData({ ...configData, apiKey: e.target.value })}
              placeholder="Sua chave de API do Kapso"
            />
            <Input
              label="Phone Number ID"
              value={configData.phoneNumberId}
              onChange={(e) => setConfigData({ ...configData, phoneNumberId: e.target.value })}
              placeholder="ID do número de telefone"
            />
            <Button type="submit" className="mt-4">
              Salvar Configuração
            </Button>
          </form>
        </div>

        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">Números Vinculados</h3>
            <Button size="sm" onClick={() => setIsNumberModalOpen(true)}>
              + Vincular Número
            </Button>
          </div>
          
          {numbers.length === 0 ? (
            <p className="text-text-secondary text-center py-8">Nenhum número vinculado</p>
          ) : (
            <div className="space-y-3">
              {numbers.map((number) => (
                <div key={number.id} className="number-card flex items-center justify-between p-3 bg-surface-1 rounded-sm">
                  <div className="number-info">
                    <h4 className="font-medium">{number.name}</h4>
                    <p className="text-sm text-text-secondary">{number.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${number.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                      {number.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteNumber(number.id)}>
                      🗑️
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium">Fila de Envio</h3>
          <Button onClick={processQueue}>
            <i className="fas fa-paper-plane"></i> Processar Fila
          </Button>
        </div>
        <p className="text-sm text-text-secondary">
          Envia todas as mensagens pendentes usando a API Kapso.
        </p>
      </div>

      <Modal
        isOpen={isNumberModalOpen}
        onClose={() => setIsNumberModalOpen(false)}
        title="Vincular Número WhatsApp"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsNumberModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => numberFormRef.current?.requestSubmit()}>Vincular</Button>
          </>
        }
      >
        <form ref={numberFormRef} onSubmit={handleCreateNumber}>
          <Input
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Número WhatsApp"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+5511999999999"
            required
          />
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-select w-full"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
};
